export type UserSearchFieldKind = 'text' | 'email' | 'phone' | 'id';

export interface UserSearchField<T> {
  key: string;
  getValue: (item: T) => unknown;
  kind?: UserSearchFieldKind;
  weight?: number;
}

export interface UserSearchConditions<T> {
  query: string;
  fields: readonly UserSearchField<T>[];
  filters?: readonly ((item: T) => boolean)[];
  minQueryLength?: number;
  limit?: number;
}

export interface UserSearchResult<T> {
  item: T;
  score: number;
  matchedFields: string[];
  matchedTokens: string[];
}

export type UserSearchQuery =
  | { mode: 'normal'; tokens: string[] }
  | { mode: 'exact_id' | 'prefix_id'; value: string };

const TOKEN_PATTERN = /[\p{L}\p{N}]+/gu;
const NON_ALPHANUMERIC_PATTERN = /[^\p{L}\p{N}]+/gu;
const DIACRITICS_PATTERN = /[\u0300-\u036f]/g;

const asSearchableStrings = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.flatMap(asSearchableStrings);
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'bigint') {
    return [String(value)];
  }

  return [];
};

const removeDiacritics = (value: string): string =>
  value.normalize('NFD').replace(DIACRITICS_PATTERN, '');

const normalizeForTokens = (value: string): string =>
  removeDiacritics(value).toLocaleLowerCase('und');

export const tokenizeUserSearch = (value: unknown): string[] => {
  const normalized = normalizeForTokens(String(value ?? ''));
  return normalized.match(TOKEN_PATTERN) ?? [];
};

export const compactUserSearchValue = (value: unknown): string => {
  const normalized = normalizeForTokens(String(value ?? ''));
  return normalized.replace(NON_ALPHANUMERIC_PATTERN, '');
};

export const parseUserSearchQuery = (query: unknown): UserSearchQuery => {
  const raw = typeof query === 'string' ? query.trim() : String(query ?? '').trim();
  const idMatch = raw.match(/^#(\d+)(\*)?$/);

  if (idMatch) {
    return {
      mode: idMatch[2] ? 'prefix_id' : 'exact_id',
      value: idMatch[1],
    };
  }

  return { mode: 'normal', tokens: tokenizeUserSearch(raw) };
};

type SearchableValue = {
  tokens: string[];
  compact: string;
  kind: UserSearchFieldKind;
};

type ScoredToken = {
  score: number;
  fieldKey: string;
};

const scoreToken = (queryToken: string, value: SearchableValue): ScoredToken | null => {
  const compactQueryToken = compactUserSearchValue(queryToken);
  if (!compactQueryToken) return null;

  const weight = value.kind === 'id' || value.kind === 'phone' ? 1.15 : 1;
  const exactMatch = value.tokens.includes(compactQueryToken) || value.compact === compactQueryToken;
  if (exactMatch) {
    return { score: 100 * weight, fieldKey: '' };
  }

  const prefixMatch = value.tokens.some((token) => token.startsWith(compactQueryToken));
  if (prefixMatch || value.compact.startsWith(compactQueryToken)) {
    return { score: 60 * weight, fieldKey: '' };
  }

  const partialMatch = value.tokens.some((token) => token.includes(compactQueryToken));
  if (partialMatch || value.compact.includes(compactQueryToken)) {
    return { score: 30 * weight, fieldKey: '' };
  }

  return null;
};

const buildSearchableValues = <T,>(
  item: T,
  fields: readonly UserSearchField<T>[],
): Map<string, SearchableValue[]> => {
  return new Map(
    fields.map((field) => [
      field.key,
      asSearchableStrings(field.getValue(item)).map((rawValue) => ({
        tokens: tokenizeUserSearch(rawValue),
        compact: compactUserSearchValue(rawValue),
        kind: field.kind ?? 'text',
      })),
    ]),
  );
};

const scoreNormalQuery = <T,>(
  item: T,
  queryTokens: string[],
  fields: readonly UserSearchField<T>[],
): Omit<UserSearchResult<T>, 'item'> | null => {
  const valuesByField = buildSearchableValues(item, fields);
  let score = 0;
  const matchedFields = new Set<string>();

  for (const queryToken of queryTokens) {
    let bestMatch: ScoredToken | null = null;
    let bestFieldKey = '';

    for (const field of fields) {
      const values = valuesByField.get(field.key) ?? [];
      for (const value of values) {
        const match = scoreToken(queryToken, value);
        if (match && (!bestMatch || match.score > bestMatch.score)) {
          bestMatch = match;
          bestFieldKey = field.key;
        }
      }
    }

    if (!bestMatch) return null;
    score += bestMatch.score * (fields.find((field) => field.key === bestFieldKey)?.weight ?? 1);
    matchedFields.add(bestFieldKey);
  }

  return {
    score,
    matchedFields: Array.from(matchedFields),
    matchedTokens: [...queryTokens],
  };
};

const scoreIdQuery = <T,>(
  item: T,
  query: Extract<UserSearchQuery, { mode: 'exact_id' | 'prefix_id' }>,
  fields: readonly UserSearchField<T>[],
): Omit<UserSearchResult<T>, 'item'> | null => {
  const idFields = fields.filter((field) => field.kind === 'id');
  const matchedFields = idFields
    .filter((field) => {
      const values = asSearchableStrings(field.getValue(item));
      return values.some((value) => {
        const compact = compactUserSearchValue(value);
        return query.mode === 'exact_id'
          ? compact === query.value
          : compact.startsWith(query.value);
      });
    })
    .map((field) => field.key);

  if (matchedFields.length === 0) return null;

  return {
    score: query.mode === 'exact_id' ? 1000 : 800,
    matchedFields,
    matchedTokens: [query.value],
  };
};

export function searchUsers<T>(
  items: readonly T[],
  conditions: UserSearchConditions<T>,
): UserSearchResult<T>[] {
  const parsedQuery = parseUserSearchQuery(conditions.query);
  const minQueryLength = conditions.minQueryLength ?? 1;

  if (
    parsedQuery.mode === 'normal' &&
    (parsedQuery.tokens.length === 0 || compactUserSearchValue(conditions.query).length < minQueryLength)
  ) {
    return [];
  }

  const filteredItems = items.filter((item) =>
    (conditions.filters ?? []).every((filter) => filter(item)),
  );

  const results = filteredItems.flatMap((item, originalIndex) => {
    const scored = parsedQuery.mode === 'normal'
      ? scoreNormalQuery(item, parsedQuery.tokens, conditions.fields)
      : scoreIdQuery(item, parsedQuery, conditions.fields);

    return scored ? [{ item, ...scored, originalIndex }] : [];
  });

  results.sort((left, right) => right.score - left.score || left.originalIndex - right.originalIndex);

  const limit = conditions.limit;
  const limited = typeof limit === 'number' && limit >= 0 ? results.slice(0, limit) : results;
  return limited.map(({ originalIndex: _originalIndex, ...result }) => result);
}
